function encodeSegment(value: string): string {
  return encodeURIComponent(value);
}

export const paths = {
  public: {
    login: '/',
    welcome: '/welcome',
    signup: '/signup',
    passwordReset: '/password-reset',
    noPermissions: '/no-permissions',
    unsubscribe: (token: string) => '/unsubscribe/' + encodeSegment(token),
    terms: '/terms',
    privacy: '/privacy',
    expertChangelog: '/experts/changelog',
    security: '/security',
    pdfPreview: '/pdf-preview',
    emailVerification: '/email-verification',
    support: '/support',
    status: '/status',
    devSmoke: '/dev/smoke',
  },
  onboarding: {
    root: '/onboarding',
    expertise: '/onboarding/expertise',
    hourlyRate: '/onboarding/hourly-rate',
    idVerification: '/onboarding/id-verification',
  },
  expert: {
    root: '/expert',
    assigned: '/expert/assigned',
    campaigns: '/expert/campaigns',
    campaignDetail: (campaignId: string) => '/expert/campaigns/' + encodeSegment(campaignId),
    campaignTimesheet: (campaignId: string) => '/expert/campaigns/' + encodeSegment(campaignId) + '/timesheet',
    earnings: '/expert/earnings',
    reviewTasks: '/expert/review-tasks',
    reviewTask: (taskId: string) => '/expert/review-tasks/' + encodeSegment(taskId),
    jobSummary: '/expert/job-summary',
    jobInstructions: (jobInstructionsId: string) => '/expert/job-instructions/' + encodeSegment(jobInstructionsId),
    analytics: '/expert/analytics',
    messageBoard: '/expert/message-board',
    knowledgeBase: '/expert/knowledge-base',
    knowledgeBaseItem: (articleId: string) => '/expert/knowledge-base/' + encodeSegment(articleId),
    training: '/expert/training',
    promptTesting2: '/expert/prompt-testing2',
    thirdPartyProxy: '/expert/third-party-proxy',
    setUpProxy: '/expert/set-up-proxy',
    partnerAccess: '/expert/partner-access',
    wideSearch: '/expert/wide-search',
    settingsPersonal: '/expert/settings/personal',
    settingsProfessional: '/expert/settings/professional',
    settingsPayments: '/expert/settings/payments',
    settingsNdaAgreement: '/expert/settings/nda-agreement',
    settingsNdaViewer: '/expert/settings/nda-viewer',
  },
  manager: {
    root: '/manager',
    projects: '/manager/projects',
    createProject: '/manager/create-project',
    project: (projectId: string) => '/manager/projects/' + encodeSegment(projectId),
    createJobForProject: (projectId: string) => '/manager/projects/' + encodeSegment(projectId) + '/create-job',
    createJob: '/manager/create-job',
    pickJobType: '/manager/pick-job-type',
    dashboard: (jobId: string) => '/manager/dashboard/' + encodeSegment(jobId),
    taskTemplates: '/manager/task-templates',
    templateBuilder: (templateId: string) => '/manager/template-builder/' + encodeSegment(templateId),
    settings: '/manager/settings',
    billing: '/manager/billing',
    datasets: '/manager/datasets',
    autoTune: '/manager/tools/auto-tune',
    createDataset: '/manager/datasets/create',
    exploreDataset: (datasetId: string) => '/manager/datasets/explore/' + encodeSegment(datasetId),
    editDataset: (datasetId: string) => '/manager/datasets/edit/' + encodeSegment(datasetId),
  },
  operations: {
    root: '/operations',
    campaigns: '/operations/campaigns',
    expertPools: '/operations/expert-pools',
    applications: '/operations/applications',
    newExpertTracker: '/operations/new-expert-tracker',
    screeningReviews: '/operations/screening-reviews',
    outreach: '/operations/outreach',
    outreachCandidates: '/operations/outreach/candidates',
    outreachDashboard: '/operations/outreach/dashboard',
    outreachSignups: '/operations/outreach/sign-ups',
    outreachSearches: '/operations/outreach/searches',
    outreachSettings: '/operations/outreach/settings',
  },
  admin: {
    root: '/admin',
    experts: '/admin/experts',
    expertsSearch: '/admin/experts/search',
    expertPools: '/admin/experts/pools',
    externalPlatforms: '/admin/experts/external-platforms',
    metaSrt: '/admin/experts/meta-srt',
    openAiFeatherExperts: '/admin/experts/openai-feather',
    cloudflareDestinations: '/admin/experts/cloudflare-destinations',
    reviewers: '/admin/experts/reviewers',
    reviewerDetail: (expertId: string) => '/admin/experts/reviewers/' + encodeSegment(expertId),
    /** Opens the reviewer detail modal over the reviewer list. */
    reviewerModal: (expertId: string) => '/admin/experts/reviewers?reviewer=' + encodeURIComponent(expertId),
    reviewerTeam: (teamId: string) => '/admin/experts/reviewer-teams/' + encodeSegment(teamId),
    /** Opens a team modal over the reviewers page's Teams tab. */
    reviewerTeamModal: (teamId: string) => '/admin/experts/reviewers?tab=teams&team=' + encodeURIComponent(teamId),
    campaigns: '/admin/campaigns',
    campaignList: '/admin/campaigns/list',
    campaignDefaults: '/admin/campaigns/defaults',
    createCampaign: '/admin/campaigns/create',
    campaign: (campaignId: string) => '/admin/campaigns/' + encodeSegment(campaignId),
    projectsJobs: '/admin/projects-jobs',
    assignExperts: '/admin/projects-jobs/assign-experts',
    assignJob: '/admin/projects-jobs/assign-job',
    jobInstructions: '/admin/projects-jobs/job-instructions',
    /** Opens one job instruction in the admin editor (the page reads `instruction-id` on load). */
    jobInstruction: (jobInstructionId: string) =>
      '/admin/projects-jobs/job-instructions?type=job&instruction-id=' + encodeURIComponent(jobInstructionId),
    mediaGallery: '/admin/projects-jobs/media-gallery',
    customers: '/admin/customers',
    customer: (customerId: string) => '/admin/customers/' + encodeSegment(customerId),
    reviews: '/admin/reviews',
    reviewTeamPools: '/admin/reviews/team-pools',
    reviewOpenAiFeather: '/admin/reviews/openai-feather',
    emails: '/admin/emails',
    emailUsers: '/admin/emails/send',
    emailTemplates: '/admin/emails/templates',
    billing: '/admin/billing',
    payPeriods: '/admin/billing/pay-periods',
    payroll: '/admin/billing/payroll',
    earningsPending: '/admin/billing/earnings-pending',
    paymentsPending: '/admin/billing/payments-pending',
    earnings: '/admin/billing/earnings',
    bonuses: '/admin/billing/bonuses',
    clientToolPayments: '/admin/billing/client-tool-payments',
    paymentTokens: '/admin/billing/payment-tokens',
    invoices: '/admin/billing/invoices',
    financeReporting: '/admin/billing/finance-reporting',
    financeExports: '/admin/billing/finance-exports',
    messageBoard: '/admin/message-board',
    opportunities: '/admin/opportunities',
    syntheticData: '/admin/synthetic-data',
    syntheticDataDashboard: '/admin/synthetic-data/dashboard',
    syntheticDataSeeds: '/admin/synthetic-data/seeds',
    syntheticDataSeedReview: (checkId: string) => '/admin/synthetic-data/seeds/review/' + encodeSegment(checkId),
    syntheticDataSeed: (seedId: string) => '/admin/synthetic-data/seeds/' + encodeSegment(seedId),
    permissions: '/admin/permissions',
    teamPermissions: '/admin/permissions/team',
    other: '/admin/other',
    customerServiceBot: '/admin/other/customer-service-bot',
  },
} as const;

/** Child route segments stay relative to their role layout route. */
export const routeSegments = {
  onboarding: {
    expertise: 'expertise',
    hourlyRate: 'hourly-rate',
    idVerification: 'id-verification',
  },
  expert: {
    assigned: 'assigned',
    campaigns: 'campaigns',
    campaignDetail: 'campaigns/:campaignId',
    campaignTimesheet: 'campaigns/:campaignId/timesheet',
    earnings: 'earnings',
    reviewTasks: 'review-tasks',
    reviewTask: 'review-tasks/:taskId',
    jobSummary: 'job-summary',
    jobInstructions: 'job-instructions/:jobInstructionsId',
    analytics: 'analytics',
    messageBoard: 'message-board',
    knowledgeBase: 'knowledge-base',
    knowledgeBaseItem: 'knowledge-base/:articleId',
    training: 'training',
    promptTesting2: 'prompt-testing2',
    thirdPartyProxy: 'third-party-proxy',
    setUpProxy: 'set-up-proxy',
    partnerAccess: 'partner-access',
    wideSearch: 'wide-search',
    settingsPersonal: 'settings/personal',
    settingsProfessional: 'settings/professional',
    settingsPayments: 'settings/payments',
    settingsNdaAgreement: 'settings/nda-agreement',
    settingsNdaViewer: 'settings/nda-viewer',
  },
  manager: {
    projects: 'projects',
    createProject: 'create-project',
    project: 'projects/:projectId',
    createJobForProject: 'projects/:projectId/create-job',
    createJob: 'create-job',
    pickJobType: 'pick-job-type',
    dashboard: 'dashboard/:jobId',
    taskTemplates: 'task-templates',
    templateBuilder: 'template-builder/:templateId',
    settings: 'settings',
    billing: 'billing',
    datasets: 'datasets',
    autoTune: 'tools/auto-tune',
    createDataset: 'datasets/create',
    exploreDataset: 'datasets/explore/:datasetId',
    editDataset: 'datasets/edit/:datasetId',
  },
  operations: {
    campaigns: 'campaigns',
    expertPools: 'expert-pools',
    applications: 'applications',
  },
  admin: {
    experts: 'experts',
    expertsSearch: 'experts/search',
    expertPools: 'experts/pools',
    externalPlatforms: 'experts/external-platforms',
    metaSrt: 'experts/meta-srt',
    openAiFeatherExperts: 'experts/openai-feather',
    cloudflareDestinations: 'experts/cloudflare-destinations',
    reviewers: 'experts/reviewers',
    reviewerDetail: 'experts/reviewers/:expertId',
    reviewerTeam: 'experts/reviewer-teams/:teamId',
    campaigns: 'campaigns',
    campaignList: 'campaigns/list',
    campaignDefaults: 'campaigns/defaults',
    createCampaign: 'campaigns/create',
    campaign: 'campaigns/:campaignId',
    projectsJobs: 'projects-jobs',
    assignExperts: 'projects-jobs/assign-experts',
    assignJob: 'projects-jobs/assign-job',
    jobInstructions: 'projects-jobs/job-instructions',
    mediaGallery: 'projects-jobs/media-gallery',
    customers: 'customers',
    customer: 'customers/:customerId',
    reviews: 'reviews',
    reviewTeamPools: 'reviews/team-pools',
    reviewOpenAiFeather: 'reviews/openai-feather',
    emails: 'emails',
    emailUsers: 'emails/send',
    emailTemplates: 'emails/templates',
    billing: 'billing',
    payPeriods: 'billing/pay-periods',
    payroll: 'billing/payroll',
    earningsPending: 'billing/earnings-pending',
    paymentsPending: 'billing/payments-pending',
    earnings: 'billing/earnings',
    bonuses: 'billing/bonuses',
    clientToolPayments: 'billing/client-tool-payments',
    paymentTokens: 'billing/payment-tokens',
    invoices: 'billing/invoices',
    financeReporting: 'billing/finance-reporting',
    financeExports: 'billing/finance-exports',
    messageBoard: 'message-board',
    opportunities: 'opportunities',
    syntheticData: 'synthetic-data',
    syntheticDataDashboard: 'synthetic-data/dashboard',
    syntheticDataSeeds: 'synthetic-data/seeds',
    syntheticDataSeedReview: 'synthetic-data/seeds/review/:checkId',
    syntheticDataSeed: 'synthetic-data/seeds/:seedId',
    permissions: 'permissions',
    teamPermissions: 'permissions/team',
    other: 'other',
    customerServiceBot: 'other/customer-service-bot',
  },
} as const;
